import React, { ComponentType, useEffect, useMemo } from 'react';

import { isEmpty } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';

import { DESCRIPTION_DIALOG_STORAGE_KEY, THEME_MODE_KEY } from 'constants/technical';
import { useAuthActions, useAuthState } from 'contexts/AuthContext';
import { authModel } from 'models';
import { ROUTES } from 'routes/constants';
import { clearLocalStorage, getCurrentHostAndProtocol } from 'utils';
import { checkHasToken, getAuthorizedRoutes } from 'utils/auth';
import { figmaModel } from 'models/FigmaModel';
import { Role, ShoppingProviderTypes } from 'types/enums';
import { getUserRole } from 'utils/getUserRole';
import { redirectToSignIn } from 'utils/helpers/urlHelpers';

const FIGMA_KEY = 'figmaKey';

export const withSignIn =
  <T,>(Component: ComponentType<T>) =>
  (props: T): JSX.Element | boolean => {
    const history = useHistory();
    const { search } = useLocation();
    const { currentUser } = useAuthState();
    const { verifyToken, toggleLoading } = useAuthActions();
    const userRole = getUserRole(currentUser.attributes.role, currentUser.attributes.clientOwner);
    const hasFirstPromoterCookies = document.cookie.split(';').some(cookie => cookie.trim().startsWith('_fprom_'));

    const authorizedRoutes = getAuthorizedRoutes(userRole);

    const redirectBuilderUser = () => {
      window.open(
        `${getCurrentHostAndProtocol()}${ROUTES.curatorNamespace}${currentUser.attributes.planogramId}`,
        '_self',
      );
    };

    useEffect(() => {
      if (!currentUser.isSignedIn) {
        // in case use from sign-in page.
        toggleLoading();
        authModel
          .verifyToken()
          .then(attributes => {
            verifyToken({
              currentUser: {
                isLoading: false,
                isSignedIn: true,
                attributes,
              },
            });
            if (!attributes.enabled) {
              redirectToSignIn();
            }
          })
          .catch(() => {
            toggleLoading();
            clearLocalStorage([DESCRIPTION_DIALOG_STORAGE_KEY, THEME_MODE_KEY]);
            redirectToSignIn();
          });
      }
    }, []);

    useEffect(() => {
      const searchParamFigmaKey = new URLSearchParams(search).get(FIGMA_KEY);

      if (searchParamFigmaKey) {
        sessionStorage.setItem(FIGMA_KEY, searchParamFigmaKey);
      }

      const sessionFigmaKey = sessionStorage.getItem(FIGMA_KEY);
      const userSignedIn = checkHasToken() && currentUser.isSignedIn;

      if (sessionFigmaKey && userSignedIn) {
        sessionStorage.removeItem(FIGMA_KEY);
        figmaModel.writeFigma({ figmaKey: sessionFigmaKey }).then(() => {
          history.push(ROUTES.connectFigma);
        });
      }
      if (!checkHasToken() || (!isEmpty(currentUser.attributes) && !currentUser.isSignedIn && !currentUser.isLoading)) {
        redirectToSignIn();
      }
    }, [currentUser, checkHasToken()]);

    const uuidPattern = '[0-9a-fA-F]{8}(-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}';
    const regexp = new RegExp(
      `${uuidPattern}|[0-9]+|(okta|${ShoppingProviderTypes.SHOPIFY}|${ShoppingProviderTypes.IDEAL})`,
    );
    const pathnameWithId = history.location.pathname.replace(regexp, ':id');

    const hasPermission = ['all', pathnameWithId].some(val => authorizedRoutes.includes(val));

    const isOtpNeedEnable = useMemo(
      () => checkHasToken() && currentUser.attributes.otpNeedEnable && !currentUser.attributes.otpRequiredForLogin,
      [currentUser],
    );

    if (hasPermission) {
      return !isEmpty(currentUser.attributes) && <Component {...props} />;
    }

    if (isOtpNeedEnable) {
      history.push(`${ROUTES.tFASettingsNew}${search}`);
    } else if (currentUser.attributes.role === Role.BUILDER_ADMIN && currentUser.attributes.planogramId) {
      if (hasFirstPromoterCookies) {
        authModel
          .trackFirstPromoterUser()
          // eslint-disable-next-line no-console
          .catch(e => console.error(e))
          .finally(redirectBuilderUser);
      } else {
        redirectBuilderUser();
      }
    } else if (authorizedRoutes.length) {
      history.push(ROUTES.page404);
    }

    return false;
  };
